import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import TextInput from "../../UI/Inputs/TextInput";
import ChevronSmall from "../../UI/Icons/ChevronSmall";
import { POSITIONS_LEVEL_OPTIONS } from "../../../constants/app";
import Checkbox from "../../UI/Inputs/Checkbox";
import KmSelector from "../../UI/KmSelector";
import { useAppSelector as useSelector } from "../../../store";

export type TSelectOptions = {
  value: string;
  label: string;
  bold: string;
};

interface CandidateFilterProps {
  setSearchInput: Dispatch<SetStateAction<string>>;
  selectedPositionsLevels: any;
  selectWorkedAtPreviously: boolean;
  selectedDistancyAway: number;
  setSelectedPositionsLevels: Dispatch<SetStateAction<any>>;
  setSelectWorkedAtPreviously: Dispatch<SetStateAction<boolean>>;
  setSelectedDistancyAway: Dispatch<SetStateAction<number>>;
}

const CandidateFilter: FC<CandidateFilterProps> = ({
  setSearchInput,
  selectedPositionsLevels,
  selectWorkedAtPreviously,
  selectedDistancyAway,
  setSelectedPositionsLevels,
  setSelectWorkedAtPreviously,
  setSelectedDistancyAway,
}) => {
  const selectedCentreDetails = useSelector(
    (state) => state.vacancy.selectedCentreDetails
  );
  
  const [isOpenFilterIcon, setIsOpenFilterIcon] = useState<boolean>(false);

  useEffect(() => {
    setSelectedDistancyAway(10)
  }, [selectedCentreDetails, setSelectedDistancyAway])

  const toggleDropdown = () => {
    setIsOpenFilterIcon(!isOpenFilterIcon);
  };

  /** Update the values in the array for designation levels or positions based on the type */
  const handleCheckBox = (value: string) => {
    if (value === "0") {
      // Toggle "Select All" - If all selected, clear; otherwise, select all
      setSelectedPositionsLevels(
        (prevValue: number[]) =>
          prevValue?.length === POSITIONS_LEVEL_OPTIONS?.length - 1
            ? [] // Deselect all
            : POSITIONS_LEVEL_OPTIONS.filter((opt) => opt.value !== "0").map(
                (opt) => Number(opt.value)
              ) // Select all
      );
    } else {
      // Toggle individual selections
      setSelectedPositionsLevels(
        (prevValue: number[]) =>
          prevValue.includes(Number(value))
            ? prevValue.filter((item) => item !== Number(value)) // Deselect
            : [...prevValue, Number(value)] // Select
      );
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const filterMenu = document.querySelector(".filter-menu");
      if (
        filterMenu &&
        !filterMenu.contains(event.target as Node) &&
        isOpenFilterIcon !== null
      ) {
        toggleDropdown(); // Close the dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpenFilterIcon]);

  const handleWorkedAtPreviouslyCheckBox = (checked: boolean) => {
    setSelectWorkedAtPreviously(checked ? true : false);
  };

  return (
    <>
      <div className="d-flex flex-column flex-lg-row gap-10 align-items-lg-center justify-content-lg-between">
        <div className="form-group w-200px mobile-w-100 input-icon icon-left">
          <TextInput
            placeholder="Search..."
            field="search"
            onChange={(e) => setSearchInput(e.target.value)}
            showSearchIcon={true}
            floatingLabel={false}
          />
        </div>
        <div className="d-flex flex-column flex-lg-row gap-25 gap-mobile-10 align-items-lg-center">
          <div className="d-flex gap-3 align-items-center">
            <div className="position-relative">
              <div
                className="d-flex gap-3 align-items-center cursor-pointer"
                onClick={toggleDropdown}
              >
                <div className="fs-13 fw-400 text-black">Show</div>
                <div className="fs-13 fw-700 text-black">
                  {selectedPositionsLevels?.length > 0
                    ? selectedPositionsLevels?.length +
                      " Levels/Positions Selected"
                    : "All Levels/Positions"}
                </div>
                <ChevronSmall isOpen={isOpenFilterIcon} />
              </div>
              {isOpenFilterIcon && (
                <div className="position-absolute end-0 d-flex flex-column  w-325px dropdown-menu">
                  <div className="filter-menu">
                    {POSITIONS_LEVEL_OPTIONS.map(
                      (
                        POSITIONS_LEVEL_OPTION: TSelectOptions,
                        index: number
                      ) => {
                        return (
                          <div key={index} className="dropdown-item">
                            <div className="theme-check">
                              <Checkbox
                                themeCheck={true}
                                className="theme-check-input"
                                id={`idx_${index}`}
                                checked={
                                  selectedPositionsLevels?.length ===
                                    POSITIONS_LEVEL_OPTIONS.length - 1 ||
                                  (selectedPositionsLevels || []).includes(
                                    Number(POSITIONS_LEVEL_OPTION.value)
                                  )
                                }
                                label={POSITIONS_LEVEL_OPTION.label}
                                onChange={() =>
                                  handleCheckBox(POSITIONS_LEVEL_OPTION.value)
                                }
                              />
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="theme-check">
            <Checkbox
              className="theme-check-input"
              id="worked_at_previously"
              checked={selectWorkedAtPreviously}
              onChange={handleWorkedAtPreviouslyCheckBox}
              labelFontSize={"13"}
              label={"Only Candidates Worked With Previously"}
              themeCheck={true}
            />
          </div>
          <div className="d-flex gap-3 align-items-center">
            <div className="fs-13 fw-400 text-black">Maximum distance away</div>
            <KmSelector
              selectedDistancyAway={selectedDistancyAway}
              setSelectedDistancyAway={setSelectedDistancyAway}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CandidateFilter;
